import { Form, message } from 'antd'
import { useEffect, useReducer } from 'react'
import { useDispatch } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { history, useTypedSelector } from '../../redux/store'
import { imgFile } from './components/brandmodules/uploadImg'
import {
  delModuleDataAction,
  setModuleDataAction,
} from '../../modules/pagecache'
import {
  GET_SUPPLIER_BRAND_DETAIL,
  SUPPLIER_BRAND_ADD,
} from '../../api/myBrand'
import {
  GetLocalStorage,
  getSupplierId,
  getUserId,
  isResponseCodeSuccess,
} from '../../tools/util'
import { useCloseTab } from '../../tools/utilHooks'
import { POST_BRANDGIVEUP_API } from '../../api/BrandApi'
import { CustomerConfirm } from '../../components/ModalUser'

interface State {
  pictures: imgFile[]
  brandDetail: any
  disabled: boolean
  loading: boolean
}

const defaultState: State = {
  pictures: [], // 预览图
  brandDetail: {},
  disabled: false,
  loading: false,
}

const ACTION_TYPE = {
  CHANGE_PIC: 'change_pic_AT', // 改变预览图
  CHANGE_DATA: 'ALLOCATIONADD/CHANGE_DATA', // 替换所有状态
  SAVE_BRANDDETAIL: 'SAVE_BRANDDETAIL',
  SET_DISABLED: 'SET_DISABLED',
  SET_BUTTONLOADING: 'SET_BUTTONLOADING',
}

const reducer = (state: State, action: { type: string; value?: any }) => {
  let arr = { ...state }
  switch (action.type) {
    case ACTION_TYPE.CHANGE_PIC:
      arr.pictures = action.value
      return arr
    case ACTION_TYPE.CHANGE_DATA:
      arr = action.value
      return arr
    case ACTION_TYPE.SAVE_BRANDDETAIL:
      arr.brandDetail = action.value
      return arr
    case ACTION_TYPE.SET_DISABLED:
      arr.disabled = action.value
      return arr
    case ACTION_TYPE.SET_BUTTONLOADING:
      arr.loading = action.value
      return arr
    default:
      return state
  }
}

// 修改logo信息
const changepicAction = (params: imgFile[]) => ({
  type: ACTION_TYPE.CHANGE_PIC,
  value: params,
})

// 重载页面数据
const reloadDataAction = (param: any) => ({
  type: ACTION_TYPE.CHANGE_DATA,
  value: param,
})

// 保存获取的品牌详情
const saveBranddetail = (param: any) => ({
  type: ACTION_TYPE.SAVE_BRANDDETAIL,
  value: param,
})

// 审核中的品牌详情页禁止操作
const setDisabled = (param: boolean) => ({
  type: ACTION_TYPE.SET_DISABLED,
  value: param,
})

const setLoading = (param: boolean) => ({
  type: ACTION_TYPE.SET_BUTTONLOADING,
  value: param,
})

const useHook = () => {
  const [initState, dispatch] = useReducer(reducer, defaultState)
  const { pictures, brandDetail, disabled } = initState
  const [form] = Form.useForm()
  const location = useLocation<{ brandId: string }>()
  const { pathname, state } = location
  const dispatchRedux = useDispatch()
  const { pagekey } = useTypedSelector((state) => state.pageCache)
  const { onCurrentPageClose } = useCloseTab()
  const dispatchPageCache = useDispatch()

  // 改变图片
  const onChangePic = (pic: imgFile[]) => {
    dispatch(changepicAction(pic))
  }

  // 校验是否编辑后的数据是相同数据，相同不调提交接口
  const isRepeat = () => {
    const formData = form.getFieldsValue()
    const formDataKeys = Object.keys(formData)
    const brandDetailKeys = Object.keys(brandDetail)
    const commonKeys = formDataKeys.filter((key) =>
      brandDetailKeys.includes(key),
    )
    for (const key of commonKeys) {
      if (formData[key] !== brandDetail[key]) {
        return false
      }
    }
    return true
  }

  // 处理品牌名称空格
  const processBrandName = (name: string) => {
    if (!name) return name
    // 去除前后空格，并将多个连续空格合并为一个
    return name.trim().replace(/\s+/g, ' ')
  }

  // 提交新增/修改品牌
  const submitBrand = async () => {
    const formData = await form.validateFields()
    const repeat = pathname === '/srm/editBrand' ? isRepeat() : false
    if (repeat && brandDetail?.mpmState !== '2') {
      cancelBrand()
      return
    }
    dispatch(setLoading(true))
    
    // 处理品牌名称的空格
    const processedFormData = {
      ...formData,
      brandNameCn: processBrandName(formData.brandNameCn),
      brandNameEn: processBrandName(formData.brandNameEn),
    }
    
    const res = await SUPPLIER_BRAND_ADD({
      ...processedFormData,
      manageType: processedFormData.manageType ? processedFormData.manageType : '1',
      brandLogoUrl: pictures.length ? pictures[0].newFilePath : '',
      id: brandDetail.id,
      brandMpmId: brandDetail.brandMpmId,
      supplierId: getSupplierId(),
      supplierName: GetLocalStorage('HOME_DATA').enterpriseName,
      supplierUserId: getUserId(),
    })
    if (isResponseCodeSuccess(res.code)) {
      dispatch(setLoading(false))
      message.success(res.msg)
      cancelBrand()
    } else {
      dispatch(setLoading(false))
      message.warning(res.msg)
    }
  }

  // 取消新增/修改品牌
  const cancelBrand = () => {
    onCurrentPageClose(() => {
      dispatchPageCache(delModuleDataAction(pathname))
    })
  }

  // 表格数据修改实时缓存数据
  const onFieldsChange = () => {
    onCache()
  }

  // 缓存数据
  const onCache = () => {
    // 缓存存数据
    const pathName = history.location.pathname
    const formData = form.getFieldsValue()
    const action = setModuleDataAction({
      key: pathName,
      data: {
        pageState: initState, // 当前状态
        formData: formData,
      },
    })
    dispatchRedux(action)
  }

  // 获取数据
  const getpagecache = () => {
    const pathName = history.location.pathname
    const data = pagekey.filter((item) => item.key === pathName)
    if (data.length === 1) {
      return data[0].data
    }
    return null
  }

  // 获取品牌详情
  const getBrandDetail = async (id: string) => {
    const res = await GET_SUPPLIER_BRAND_DETAIL({ brandId: id })
    if (isResponseCodeSuccess(res.code)) {
      dispatch(saveBranddetail(res.data))
      form.setFieldsValue(res.data)
      // 如果之前提交时为上传logo则不修改数据，避免进入品牌详情页展示logo异常
      if (res.data.brandLogoUrl) {
        dispatch(
          changepicAction([
            {
              baseUrl: GetLocalStorage('BASEURL'),
              newFilePath: res.data.brandLogoUrl,
            },
          ]),
        )
      }
      dispatch(setDisabled(res.data.mpmState === '1' ? true : false))
      onCache()
    }
  }

  // 放弃
  const nopassSwitch = () => {
    CustomerConfirm({
      title: '提示',
      content: '确认放弃申请？',
      onOk: () => {
        givedata(String(brandDetail.id))
      },
    })
  }

  // 批量 放弃申请
  const givedata = async (assessId: string) => {
    message.loading('放弃中', 0)
    const resData = await POST_BRANDGIVEUP_API({ brandId: assessId })
    if (resData.code === 0) {
      message.success('已放弃')
      cancelBrand()
    } else {
      message.destroy()
    }
  }

  useEffect(() => {
    const flag = getpagecache()
    if (flag && !state) {
      form.setFieldsValue(flag.formData)
      const action = reloadDataAction(flag.pageState)
      dispatch(action)
      if (history.location.pathname.split('/')[2] === 'brandDetail') {
        dispatch(setDisabled(true))
      }
    } else if (state && state.brandId) {
      // 根据当前品牌id获取详情信息
      getBrandDetail(state.brandId)
    }
    // eslint-disable-next-line
  }, [])

  // 品牌中文名校验
  const validatorNameCn = (rule: any, value: string) => {
    const regGrossweight = /^[\u4e00-\u9fa5a-zA-Z0-9+\-.&（） ]{1,50}$/
    if (!value || value.trim() === '') {
      return Promise.reject('请输入中文名')
    } else if (!regGrossweight.test(value)) {
      return Promise.reject(
        '请输入中文汉字、英文字母、数字、空格字符和部分特殊字符+，-，(，)，(.)，&，( )；1-50字符以内',
      )
    }
    return Promise.resolve()
  }
  // 品牌英文名校验
  const validatorNameEn = (rule: any, value: string) => {
    const regGrossweight = /^[a-zA-Z0-9+\-().& ]{1,50}$/
    if (!value || value.trim() === '') {
      return Promise.reject('请输入英文名')
    } else if (!regGrossweight.test(value)) {
      return Promise.reject(
        '请输入英文字母、数字、空格字符和部分特殊字符+，-，(，)，(.)，&，( )；1-50字符以内',
      )
    }
    return Promise.resolve()
  }
  return {
    state: initState,
    form,
    pictures,
    brandDetail,
    disabled,
    onChangePic,
    submitBrand,
    cancelBrand,
    onFieldsChange,
    nopassSwitch,
    validatorNameCn,
    validatorNameEn,
  }
}

export default useHook
import { Form, message } from 'antd'
import { useEffect, useReducer } from 'react'
import { useDispatch } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { history, useTypedSelector } from '../../redux/store'
import { imgFile } from './components/brandmodules/uploadImg'
import {
  delModuleDataAction,
  setModuleDataAction,
} from '../../modules/pagecache'
import {
  GET_SUPPLIER_BRAND_DETAIL,
  SUPPLIER_BRAND_ADD,
} from '../../api/myBrand'
import {
  GetLocalStorage,
  getSupplierId,
  getUserId,
  isResponseCodeSuccess,
} from '../../tools/util'
import { useCloseTab } from '../../tools/utilHooks'
import { POST_BRANDGIVEUP_API } from '../../api/BrandApi'
import { CustomerConfirm } from '../../components/ModalUser'

interface State {
  pictures: imgFile[]
  brandDetail: any
  disabled: boolean
  loading: boolean
}

const defaultState: State = {
  pictures: [], // 预览图
  brandDetail: {},
  disabled: false,
  loading: false,
}

const ACTION_TYPE = {
  CHANGE_PIC: 'change_pic_AT', // 改变预览图
  CHANGE_DATA: 'ALLOCATIONADD/CHANGE_DATA', // 替换所有状态
  SAVE_BRANDDETAIL: 'SAVE_BRANDDETAIL',
  SET_DISABLED: 'SET_DISABLED',
  SET_BUTTONLOADING: 'SET_BUTTONLOADING',
}

const reducer = (state: State, action: { type: string; value?: any }) => {
  let arr = { ...state }
  switch (action.type) {
    case ACTION_TYPE.CHANGE_PIC:
      arr.pictures = action.value
      return arr
    case ACTION_TYPE.CHANGE_DATA:
      arr = action.value
      return arr
    case ACTION_TYPE.SAVE_BRANDDETAIL:
      arr.brandDetail = action.value
      return arr
    case ACTION_TYPE.SET_DISABLED:
      arr.disabled = action.value
      return arr
    case ACTION_TYPE.SET_BUTTONLOADING:
      arr.loading = action.value
      return arr
    default:
      return state
  }
}

// 修改logo信息
const changepicAction = (params: imgFile[]) => ({
  type: ACTION_TYPE.CHANGE_PIC,
  value: params,
})

// 重载页面数据
const reloadDataAction = (param: any) => ({
  type: ACTION_TYPE.CHANGE_DATA,
  value: param,
})

// 保存获取的品牌详情
const saveBranddetail = (param: any) => ({
  type: ACTION_TYPE.SAVE_BRANDDETAIL,
  value: param,
})

// 审核中的品牌详情页禁止操作
const setDisabled = (param: boolean) => ({
  type: ACTION_TYPE.SET_DISABLED,
  value: param,
})

const setLoading = (param: boolean) => ({
  type: ACTION_TYPE.SET_BUTTONLOADING,
  value: param,
})

const useHook = () => {
  const [initState, dispatch] = useReducer(reducer, defaultState)
  const { pictures, brandDetail, disabled } = initState
  const [form] = Form.useForm()
  const location = useLocation<{ brandId: string }>()
  const { pathname, state } = location
  const dispatchRedux = useDispatch()
  const { pagekey } = useTypedSelector((state) => state.pageCache)
  const { onCurrentPageClose } = useCloseTab()
  const dispatchPageCache = useDispatch()

  // 改变图片
  const onChangePic = (pic: imgFile[]) => {
    dispatch(changepicAction(pic))
  }

  // 校验是否编辑后的数据是相同数据，相同不调提交接口
  const isRepeat = () => {
    const formData = form.getFieldsValue()
    const formDataKeys = Object.keys(formData)
    const brandDetailKeys = Object.keys(brandDetail)
    const commonKeys = formDataKeys.filter((key) =>
      brandDetailKeys.includes(key),
    )
    for (const key of commonKeys) {
      if (formData[key] !== brandDetail[key]) {
        return false
      }
    }
    return true
  }

  // 处理品牌名称空格
  const processBrandName = (name: string) => {
    if (!name) return name
    // 去除前后空格，并将多个连续空格合并为一个
    return name.trim().replace(/\s+/g, ' ')
  }

  // 提交新增/修改品牌
  const submitBrand = async () => {
    const formData = await form.validateFields()
    const repeat = pathname === '/srm/editBrand' ? isRepeat() : false
    if (repeat && brandDetail?.mpmState !== '2') {
      cancelBrand()
      return
    }
    dispatch(setLoading(true))
    
    // 处理品牌名称的空格
    const processedFormData = {
      ...formData,
      brandNameCn: processBrandName(formData.brandNameCn),
      brandNameEn: processBrandName(formData.brandNameEn),
    }
    
    const res = await SUPPLIER_BRAND_ADD({
      ...processedFormData,
      manageType: processedFormData.manageType ? processedFormData.manageType : '1',
      brandLogoUrl: pictures.length ? pictures[0].newFilePath : '',
      id: brandDetail.id,
      brandMpmId: brandDetail.brandMpmId,
      supplierId: getSupplierId(),
      supplierName: GetLocalStorage('HOME_DATA').enterpriseName,
      supplierUserId: getUserId(),
    })
    if (isResponseCodeSuccess(res.code)) {
      dispatch(setLoading(false))
      message.success(res.msg)
      cancelBrand()
    } else {
      dispatch(setLoading(false))
      message.warning(res.msg)
    }
  }

  // 取消新增/修改品牌
  const cancelBrand = () => {
    onCurrentPageClose(() => {
      dispatchPageCache(delModuleDataAction(pathname))
    })
  }

  // 表格数据修改实时缓存数据
  const onFieldsChange = () => {
    onCache()
  }

  // 缓存数据
  const onCache = () => {
    // 缓存存数据
    const pathName = history.location.pathname
    const formData = form.getFieldsValue()
    const action = setModuleDataAction({
      key: pathName,
      data: {
        pageState: initState, // 当前状态
        formData: formData,
      },
    })
    dispatchRedux(action)
  }

  // 获取数据
  const getpagecache = () => {
    const pathName = history.location.pathname
    const data = pagekey.filter((item) => item.key === pathName)
    if (data.length === 1) {
      return data[0].data
    }
    return null
  }

  // 获取品牌详情
  const getBrandDetail = async (id: string) => {
    const res = await GET_SUPPLIER_BRAND_DETAIL({ brandId: id })
    if (isResponseCodeSuccess(res.code)) {
      dispatch(saveBranddetail(res.data))
      form.setFieldsValue(res.data)
      // 如果之前提交时为上传logo则不修改数据，避免进入品牌详情页展示logo异常
      if (res.data.brandLogoUrl) {
        dispatch(
          changepicAction([
            {
              baseUrl: GetLocalStorage('BASEURL'),
              newFilePath: res.data.brandLogoUrl,
            },
          ]),
        )
      }
      dispatch(setDisabled(res.data.mpmState === '1' ? true : false))
      onCache()
    }
  }

  // 放弃
  const nopassSwitch = () => {
    CustomerConfirm({
      title: '提示',
      content: '确认放弃申请？',
      onOk: () => {
        givedata(String(brandDetail.id))
      },
    })
  }

  // 批量 放弃申请
  const givedata = async (assessId: string) => {
    message.loading('放弃中', 0)
    const resData = await POST_BRANDGIVEUP_API({ brandId: assessId })
    if (resData.code === 0) {
      message.success('已放弃')
      cancelBrand()
    } else {
      message.destroy()
    }
  }

  useEffect(() => {
    const flag = getpagecache()
    if (flag && !state) {
      form.setFieldsValue(flag.formData)
      const action = reloadDataAction(flag.pageState)
      dispatch(action)
      if (history.location.pathname.split('/')[2] === 'brandDetail') {
        dispatch(setDisabled(true))
      }
    } else if (state && state.brandId) {
      // 根据当前品牌id获取详情信息
      getBrandDetail(state.brandId)
    }
    // eslint-disable-next-line
  }, [])

  // 品牌中文名校验
  const validatorNameCn = (rule: any, value: string) => {
    const regGrossweight = /^[\u4e00-\u9fa5a-zA-Z0-9+\-.&（） ]{1,50}$/
    if (!value || value.trim() === '') {
      return Promise.reject('请输入中文名')
    } else if (!regGrossweight.test(value)) {
      return Promise.reject(
        '请输入中文汉字、英文字母、数字、空格字符和部分特殊字符+，-，(，)，(.)，&，( )；1-50字符以内',
      )
    }
    return Promise.resolve()
  }
  // 品牌英文名校验
  const validatorNameEn = (rule: any, value: string) => {
    const regGrossweight = /^[a-zA-Z0-9+\-().& ]{1,50}$/
    if (!value || value.trim() === '') {
      return Promise.reject('请输入英文名')
    } else if (!regGrossweight.test(value)) {
      return Promise.reject(
        '请输入英文字母、数字、空格字符和部分特殊字符+，-，(，)，(.)，&，( )；1-50字符以内',
      )
    }
    return Promise.resolve()
  }
  return {
    state: initState,
    form,
    pictures,
    brandDetail,
    disabled,
    onChangePic,
    submitBrand,
    cancelBrand,
    onFieldsChange,
    nopassSwitch,
    validatorNameCn,
    validatorNameEn,
  }
}

export default useHook
